import React, { useEffect, useRef, useState } from "react";

type Props = {
  /** Display name e.g. "Decking" */
  name: string;
  /** Optional formatted subtotal — "12,340" (no $). When provided, the
   *  collapsed state shows it on the right; when null/0, shows "Tap to
   *  configure" instead. */
  subtotal?: number | null;
  /** When true, the card is in a "configured" state — green check on the
   *  left, subtotal shown. Round 2 will compute this from real selections. */
  configured?: boolean;
  /** When true, draws the blue "active step" outline. Parent decides
   *  which card is currently active in the guided flow. */
  active?: boolean;
  /** Fires whenever the user taps the card head (open OR close). Parent
   *  uses this to advance the active-step highlight past empty cards
   *  the user intends to skip. Tap is a user gesture, not programmatic,
   *  so this stays safe re: iOS keyboard / scroll quirks. */
  onActivate?: () => void;
  /** Whether the expanded body is currently editable; for Round 1 the
   *  body is a placeholder. */
  children?: React.ReactNode;
};

const moneyComma = (n: number) =>
  (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * LineItemCard — collapsed/expanded mobile card.
 *
 * On open:
 *  1. Scrolls the card to the top of the scroll viewport so its inputs
 *     are immediately visible under the sticky header.
 *  2. Auto-triggers the native <select> picker in the body via
 *     HTMLSelectElement.showPicker() so the user doesn't have to tap
 *     a second time. Must run inside the user-activation window from
 *     the head tap; we defer one frame so the body is mounted, then
 *     call showPicker synchronously. iOS Safari supports this from
 *     17.4+; older browsers fall back to focusing the select (no
 *     picker pop, but at least signals intent).
 *
 * Skipped on close.
 */
export default function LineItemCard({
  name,
  subtotal = null,
  configured = false,
  active = false,
  onActivate,
  children,
}: Props) {
  const [open, setOpen] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const handleToggle = () => {
    onActivate?.();
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        // Wait one frame so the expanded body is laid out before scrolling;
        // otherwise scrollIntoView measures the collapsed height.
        requestAnimationFrame(() => {
          sectionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      }
      return next;
    });
  };

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      const select = bodyRef.current?.querySelector(
        "select:not([disabled])"
      ) as HTMLSelectElement | null;
      if (!select) return;
      // showPicker exists on iOS Safari 17.4+, Chrome 99+, etc. Older
      // browsers: best-effort focus so the user at least lands on the
      // control.
      const maybePicker = (select as unknown as {
        showPicker?: () => void;
      }).showPicker;
      try {
        if (typeof maybePicker === "function") {
          maybePicker.call(select);
          return;
        }
      } catch {
        // Activation window may have lapsed; fall through to focus.
      }
      select.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  return (
    <section
      className={"m-line-card" + (active ? " is-active-step" : "")}
      aria-label={name}
      ref={sectionRef}
    >
      <button
        type="button"
        className="m-line-head"
        onClick={handleToggle}
        aria-expanded={open}
      >
        <div>
          <div className="m-line-name">
            {configured && (
              <span className="m-line-check" aria-hidden>
                ✓
              </span>
            )}
            <span>{name}</span>
          </div>
          {!configured && (
            <div className="m-line-sub">Tap to configure</div>
          )}
        </div>
        <div className="m-line-right">
          {configured && subtotal != null && subtotal > 0 ? (
            <span className="m-line-amount">{moneyComma(subtotal)}</span>
          ) : null}
          <span className="m-line-caret" aria-hidden>
            {open ? "▾" : "▸"}
          </span>
        </div>
      </button>

      {open && (
        <div className="m-line-body" ref={bodyRef}>
          {children ?? (
            <div className="m-placeholder-sub" style={{ textAlign: "left" }}>
              Inputs for <strong>{name}</strong> ship in Round&nbsp;2. This card
              is a placeholder so you can verify layout and tap behavior on
              your phone.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
